import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/styles'
import { Link } from 'react-router-dom'
import ViewTitle from '../../../components/ViewTitle'
import { TitleTypography, Header, ContentWrapper, ContentText, ReferenceLink, Numberedhead } from './styles'
import { EXTERNAL_ROUTES, INTERNAL_ROUTES } from '../../../config/routes'

export default function BiometricPolicy() {
  const theme = useTheme()

  return (
    <Box component="main" sx={{ maxWidth: theme.breakpoints.values.xl, width: '100%', margin: '0 auto' }}>
      <ViewTitle title="Biometric Information Privacy Policy" />
      <Header>
        <Typography
          variant="h1"
          id="mainTitle"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' },
            color: '#fff',
            textAlign: 'center'
          }}
        >
          Photobucket Biometric Information Privacy Policy
        </Typography>
      </Header>
      <ContentWrapper>
        <TitleTypography>Effective Date: July 22, 2024</TitleTypography>
        <Numberedhead>1. INTRODUCTION</Numberedhead>
        <ContentText>
          We care about your privacy and the security of your Biometric Information. This Photobucket Biometric Information Privacy Policy (the
          “Biometric Policy”) defines the rights you grant Photobucket.com, Inc. (“Photobucket,” “we,” “our,” or “us”) regarding your Biometric
          Information, clearly describes how we collect, retain, use, disclose and protect your Biometric Information, and outlines the tools you have
          to exercise control over your Biometric Information. This Biometric Policy, together with Photobucket’s{' '}
          <ReferenceLink to={INTERNAL_ROUTES.PRIVACY_POLICY} component={Link}>
            Privacy Policy
          </ReferenceLink>
          ,{' '}
          <ReferenceLink to={INTERNAL_ROUTES.TERMS_OF_SERVICE} component={Link}>
            Terms of Use
          </ReferenceLink>
          ,
          <ReferenceLink to={INTERNAL_ROUTES.COPYRIGHT_POLICY} component={Link}>
            Copyright and Intellectual Property Policy
          </ReferenceLink>
          , and any other agreement between you and Photobucket related to the Services (collectively, the “Agreements”) set forth the terms on which
          Photobucket provides the Services to you. Capitalized terms are defined below{' '}
          <ReferenceLink to={INTERNAL_ROUTES.PRIVACY_POLICY} component={Link}>
            in our Privacy Policy
          </ReferenceLink>{' '}
          or in our{' '}
          <ReferenceLink to={INTERNAL_ROUTES.TERMS_OF_SERVICE} component={Link}>
            Terms of Use
          </ReferenceLink>
          .
        </ContentText>
        <ContentText>
          By agreeing to this Biometric Policy you grant Photobucket, to the extent permitted by the laws of your region, the right to sell, lease,
          trade or otherwise profit from the disclosure of your Public User Uploaded Content which may contain Biometric Information that may be
          extracted.
        </ContentText>
        <Numberedhead>2. DEFINITIONS</Numberedhead>
        <ContentText>
          <strong>Biometric Information Definition:</strong> Biometric Information refers to data that depict or describe physical, biological, or
          behavioral traits, characteristics, or measurements of or relating to an identified or identifiable person’s body. Biometric Information
          includes, but is not limited to, depictions, images, descriptions, or recordings of an individual’s facial features, iris or retina, finger
          or handprints, voice, genetics, or characteristic movements or gestures (e.g., gait or typing pattern). Biometric Information also includes
          data derived from such depictions, images, descriptions, or recordings, to the extent that it would be reasonably possible to identify the
          person from whose information the data had been derived. By way of example, both a photograph of a person’s face and a facial recognition
          template, embedding, faceprint, or other data that encode measurements or characteristics of the face depicted in the photograph constitute
          Biometric Information. There are different definitions for biometric information, or biometric data, under different state laws. Within this
          Biometric Policy we generally adopt the Federal Trade Commission’s (“FTC”) definition.
        </ContentText>
        <ContentText>
          <strong>Legacy Free Account Definition:</strong> “Legacy Free Account” refers to the accounts for services that Photobucket previously
          allowed users to sign up for which allowed users to upload and store Content through the Photobucket Services free of charge. See Terms of
          Use for more information about Legacy Free Accounts.
        </ContentText>
        <ContentText>
          <strong>Paid Account Definition:</strong> “Paid Account” refers to the several paid options that give the Subscriber more storage, image
          hosting and other services. See Terms of Use for more information about Paid Accounts.
        </ContentText>
        <ContentText>
          <strong>User Uploaded Content Definition:</strong> Our Services allow you to upload the most commonly-used image and video files. These
          image files and video files, both in their native uploaded form and any subsequently edited version stored with the Services, are referred
          to individually and collectively as “User Uploaded Content.” User Uploaded Content may be Private User Uploaded Content or Public User
          Uploaded Content or Shareable User Uploaded Content.
        </ContentText>
        <ContentText>
          <strong>Private User Uploaded Content Definition:</strong> In previous versions of Photobucket Services, a Subscriber with an Account could
          mark an album containing User Uploaded Content as “private” (“Private User Uploaded Content”).
        </ContentText>
        <ContentText>
          <strong>Public User Uploaded Content Definition:</strong> In previous versions of Photobucket Services, you could upload and share images
          and information by marking albums as “Public”, allowing them to be viewed, copied, downloaded, or used by others. If you intended to enable
          such interactions in this manner, others could: (a) tag, describe, or comment on images, whether hosted by you or someone else; (b) make
          comments on a forum; or (c) click an icon next to another user’s image or comment to convey agreement, disagreement, or thanks. This content
          is referred to as “Public User Uploaded Content” because you intended such public interactions. The concept of “Public” from the past
          versions of Services offered by Photobucket no longer exists for new uploads of User Uploaded Content as of February 6, 2024. User Uploaded
          Content or albums in the current version of the “Services that were Public User Uploaded Content” prior to February 6, 2024, remains Public,
          for Legacy Free Accounts. User Uploaded Content uploaded as of February 6, 2024, is not Public.
        </ContentText>
        <ContentText>
          <strong>Sharable User Uploaded Content Definition:</strong> Albums and User Uploaded Content in the current version of the Services that
          were uploaded from a Paid Account, are not Public User Uploaded Content because they are shareable instead of Public (“Sharable User
          Uploaded Content”). Public User Uploaded Content prior to February 6, 2024, for Paid Accounts was converted to Shareable User Uploaded
          Content as of February 6, 2024, because in the current version of the Services, albums with User Uploaded Content can be shared by creating
          a link. This link can be viewed by those with access to it and can also be password protected. These links are referred to as “Sharing
          Links”. If you have questions about your User Uploaded Content, please contact us at{' '}
          <ReferenceLink href={EXTERNAL_ROUTES.SUPPORT_EMAIL}>support@photobucket.com</ReferenceLink>.
        </ContentText>
        <Numberedhead>3. BIOMETRIC INFORMATION COLLECTED AND PURPOSE</Numberedhead>
        <ContentText>
          Our Services include User Uploaded Content storage services which may involve storing your Biometric Information. For instance, if you
          voluntarily upload a photo containing your face to our Services, we will store it to provide the Service you requested. By agreeing to our
          use of your Biometric Information you grant Photobucket, to the extent permitted by the laws of your region, commercialize, including the
          right to license your Public User Uploaded Content to third parties for the scanning and processing of the Public User Uploaded Content,
          including extracting physical features, e.g. measurements, of your Biometric Information (e.g., face, iris, etc.), for the purpose of
          artificial intelligence and machine learning training and the subsequent uses derived therefrom. Outside the User Uploaded Content you store
          on our Services and rights granted above, Photobucket does not collect, use, or store your Biometric Information outside of in the form of
          User Uploaded Content. Photobucket will not collect, use, store, disclose, or allow a third party to do any of these activities, if you
          opt-out.
        </ContentText>
        <Numberedhead>4. REGION SPECIFIC RESIDENT DISCLOSURE</Numberedhead>
        <ContentText>
          Public User Uploaded Content from residents of California, Colorado, Connecticut, Florida, Illinois, Montana, Oregon, Texas, Utah, Virginia,
          Washington, and the European Union will not knowingly be sold, leased, traded, or otherwise profited from even if a user agrees to its sale,
          lease, trade, or otherwise profit from the disclosure of their Public User Uploaded Content. Out of respect for your privacy, Photobucket
          limits the amount of geolocation information it possesses; therefore, if you have become a resident of one of these states since you last
          signed up for your Photobucket account, or if you want to confirm your state residency with us, please contact us at support@photobucket.com
          or update your user profile once logged into your Account.
        </ContentText>
        <Numberedhead>5. RETENTION AND STORAGE</Numberedhead>
        <ContentText>
          If you provide us User Uploaded Content that contains your Biometric Information we will retain and store that User Uploaded Content to
          provide the Service you requested. Photobucket does not scan or process your User Uploaded Content to extract your Biometric Information.
          Photobucket may allow a third party to collect, use, and/or store your Biometric Information as explained in Section 3 (Biometric
          Information Collected and Purpose) above. In any agreement between Photobucket and a third party to sell, lease, trade or otherwise profit
          from the disclosure of your Biometric Information from your Public User Uploaded Content, Photobucket will contractually require that the
          third party collect, store, transmit, and protect your Biometric Information using a reasonable standard of care that is at least as
          protective as the way Photobucket collects, stores, transmits, and protects other confidential and sensitive personal information.
          Additionally, in any agreement between Photobucket and a third party to sell, lease, trade or otherwise profit from the disclosure of your
          Biometric Information, Photobucket will contractually require that the third party, unless otherwise required by law or court order, only
          retain the Biometric Information until the initial purpose for collecting the Biometric Information has been satisfied, unless legally
          required to keep it for a different period.
        </ContentText>
        <Numberedhead>6. OPTING OUT AND QUESTIONS</Numberedhead>
        <ContentText>
          Please click <ReferenceLink href={EXTERNAL_ROUTES.DELETE_ACCOUNT}>here</ReferenceLink> or contact us at{' '}
          <ReferenceLink href={EXTERNAL_ROUTES.SUPPORT_EMAIL}>support@photobucket.com</ReferenceLink> if you would like to opt-out from granting
          Photobucket the right to sell, lease, trade or otherwise profit from Biometric Information contained in your Public User Uploaded Content.
          Opting out requires account deletion.
        </ContentText>
        <ContentText>
          There is an initial opt-out period of forty-five (45) days from the Effective Date listed above (“Initial Opt-Out Period”). During the
          Initial Opt-Out Period, you can opt-out before any license or sale takes effect.{' '}
          <strong>
            <em>
              Please note that if you opt-out, Photobucket will delete your Account and all of your User Uploaded Content. Therefore, if you decide to
              opt-out, please download and save any of your User Uploaded Content you wish to keep and store.
            </em>
          </strong>
        </ContentText>
        <ContentText>
          <strong>
            <em>
              After the end of the Initial Opt-Out Period, you will still have the option to opt-out from granting Photobucket the right to sell,
              lease, trade or otherwise profit from the disclosure of your Biometric Information contained in your Public User Uploaded Content
              prospectively. If you do not opt out during the Initial Opt-Out Period and we do provide your Public User Uploaded Content to a third
              party, we cannot guarantee we can recall it, but it will not be further sold, leased, traded, or otherwise profited from after the date
              we receive and process your opt-out request.
            </em>
          </strong>
        </ContentText>
        <ContentText>
          If you have any questions about this Biometric Policy, please contact Photobucket at
          <ReferenceLink href={EXTERNAL_ROUTES.SUPPORT_EMAIL}>support@photobucket.com</ReferenceLink>.
        </ContentText>
      </ContentWrapper>
    </Box>
  )
}
